import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Upload, ListChecks, Download, AlertTriangle, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { axiosInstance } from "@/App";

const HEADER = "UC;Status Rateio;UC da Usina";
const SAMPLE = `${HEADER}\n55358532;Incluido;1231244345\n23422751;Pausado;`;

const parseRows = (text) => {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const rows = [];
  for (const line of lines) {
    if (/^uc\s*[;,\t]/i.test(line)) continue;
    const parts = line.split(/[;\t,]/).map((p) => p.trim().replace(/^"|"$/g, ""));
    if (!parts[0]) continue;
    rows.push({
      installation_number: parts[0],
      apportionment_status: parts[1] || "",
      generator_installation_number: parts[2] || "",
    });
  }
  return rows;
};

export const CooperativesBulkUpdate = ({ open, onOpenChange, onApplied }) => {
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const rows = useMemo(() => parseRows(text), [text]);

  const reset = () => { setText(""); setPreview(null); };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { setText(String(reader.result || "")); setPreview(null); };
    reader.readAsText(f, "utf-8");
    e.target.value = "";
  };

  const downloadTemplate = () => {
    const blob = new Blob(["\uFEFF" + SAMPLE], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "modelo_atualizacao_massa_cooperados.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const doPreview = async () => {
    if (!rows.length) return toast.error("Cole ou importe as linhas primeiro");
    setLoading(true);
    try {
      const r = await axiosInstance.post("/cooperatives/management/bulk-preview", { rows });
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao validar linhas");
    } finally { setLoading(false); }
  };

  const doApply = async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.post("/cooperatives/management/bulk-apply", { rows });
      toast.success(`${r.data.updated} cooperado(s) atualizado(s)${r.data.skipped ? ` · ${r.data.skipped} linha(s) ignorada(s)` : ""}`);
      onApplied?.();
      onOpenChange(false);
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao aplicar atualização");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="bulk-update-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ListChecks className="w-5 h-5 text-blue-700" /> Atualização em Massa</DialogTitle>
          <DialogDescription>
            Uma linha por cooperado: <code className="bg-gray-100 px-1 rounded">UC; Status Rateio; UC da Usina</code>.
            Deixe em branco o que não quiser alterar. A usina é identificada pela UC da geradora e o nome é preenchido automaticamente.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2 items-center">
          <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="bulk-update-template-btn">
            <Download className="w-4 h-4 mr-1" /> Baixar modelo CSV
          </Button>
          <label className="inline-flex">
            <input type="file" accept=".csv,.txt" className="hidden" onChange={handleFile} data-testid="bulk-update-file-input" />
            <span className="inline-flex items-center h-9 px-3 rounded-md border text-sm cursor-pointer hover:bg-gray-50">
              <Upload className="w-4 h-4 mr-1" /> Importar CSV
            </span>
          </label>
          <span className="text-xs text-gray-500">Status válidos: Aguardando · Pedido de Troca · Incluido · Excluido · Pausado</span>
        </div>

        <Textarea
          value={text}
          onChange={(e) => { setText(e.target.value); setPreview(null); }}
          placeholder={SAMPLE}
          rows={6}
          className="font-mono text-xs"
          data-testid="bulk-update-textarea"
        />

        {preview && (
          <div className="border rounded-md overflow-x-auto">
            <div className="flex gap-4 text-xs px-3 py-2 bg-gray-50 border-b">
              <span className="inline-flex items-center gap-1 text-green-700" data-testid="bulk-update-valid-count"><CheckCircle2 className="w-3.5 h-3.5" /> {preview.valid} válida(s)</span>
              <span className="inline-flex items-center gap-1 text-amber-700" data-testid="bulk-update-invalid-count"><AlertTriangle className="w-3.5 h-3.5" /> {preview.invalid} com erro (serão ignoradas)</span>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>UC</TableHead>
                  <TableHead>Cooperado</TableHead>
                  <TableHead>Status Rateio</TableHead>
                  <TableHead>UC da Usina</TableHead>
                  <TableHead>Usina</TableHead>
                  <TableHead>Situação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.rows.map((r, i) => (
                  <TableRow key={i} className={r.errors.length ? "bg-amber-50/60" : ""} data-testid={`bulk-update-row-${i}`}>
                    <TableCell className="font-mono text-xs">{r.installation_number}</TableCell>
                    <TableCell className="text-xs">{r.cooperative_name || <span className="text-gray-400">—</span>}</TableCell>
                    <TableCell className="text-xs">
                      {r.apportionment_status ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="text-gray-400">{r.current_apportionment_status || "Aguardando"}</span>
                          <ArrowRight className="w-3 h-3 text-gray-400" />
                          <b>{r.apportionment_status}</b>
                        </span>
                      ) : <span className="text-gray-400">mantém</span>}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{r.generator_installation_number || <span className="text-gray-400">—</span>}</TableCell>
                    <TableCell className="text-xs" data-testid={`bulk-update-plant-${i}`}>
                      {r.powerplant_name ? <b>{r.powerplant_name}</b> : r.generator_installation_number ? <span className="text-amber-700">não encontrada</span> : <span className="text-gray-400">mantém</span>}
                    </TableCell>
                    <TableCell className="text-xs">
                      {r.errors.length
                        ? <span className="text-amber-700">{r.errors.join("; ")}</span>
                        : <span className="text-green-700">OK</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <DialogFooter className="gap-2">
          <span className="text-xs text-gray-500 mr-auto self-center">{rows.length} linha(s) detectada(s)</span>
          <Button variant="outline" onClick={doPreview} disabled={loading || !rows.length} data-testid="bulk-update-preview-btn">
            Validar
          </Button>
          <Button onClick={doApply} disabled={loading || !preview || !preview.valid} data-testid="bulk-update-apply-btn">
            Aplicar {preview?.valid ? `(${preview.valid})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
